// Typed wrappers for the three /v1/merchants/:id/bank-accounts endpoints.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Response / payload interfaces
// ---------------------------------------------------------------------------

/**
 * BankAccount — the bank-rail representation returned by the backend.
 * No account_no or ifsc fields (D-05): those columns are nullable and never
 * surfaced to the client.
 * `status` is derived by the backend from `disabled_at`:
 *   disabled_at != null → "pending" (credentials not yet verified)
 *   disabled_at == null → "active"
 */
export interface BankAccount {
  id: number;
  merchant_id: number;
  label: string;
  provider: PublicBankProvider;
  currency: string;
  status: "active" | "pending" | "disabled";
  created_at: string;
  disabled_at: string | null;
  /**
   * The operator's off switch (backend migration 050). When true the rail is
   * skipped by routing whatever a rule's priority says, refuses new payouts,
   * and refuses to dispatch ones already queued against it.
   *
   * NOT the same as `disabled_at`, which means the rail's credentials have
   * never verified — a half-built rail, cleared by a passing Test connection.
   * A rail can be either, both, or neither, and `status` collapses the pair
   * for display with `disabled` winning.
   *
   * Optional on the wire so older responses don't break the type; treat
   * undefined as false (not disabled), which matches the column default.
   */
  disabled?: boolean;
  /** Why the rail was switched off. Only meaningful while `disabled`. */
  disabled_reason?: string | null;
  /**
   * Per-rail auto-approve (backend migration 034). When true, payouts on
   * this rail dispatch inline without admin approval. Optional on the wire
   * so legacy responses don't break the type — treat undefined as false.
   */
  auto_approve?: boolean;
  /** Present only on a fixed-format file rail. */
  file_rail_settings?: FileRailSettings;
  /** The account transfers are debited from. Present on file rails. */
  account_no?: string | null;
  ifsc?: string | null;
}

/**
 * BankProvider — internal routing key the panel sends on CREATE / SELECT
 * requests. These match the backend's internal provider constants and are
 * still the values driving the AddRailModal dropdown.
 *   - "paysmart_idfc"          — IDFC IMPS/NEFT via the bank rail (per-rail creds)
 *   - "buckzy_global_payout"   — Buckzy global (per-merchant creds)
 *   - "razorpayx_inr"          — RazorpayX (per-rail creds; key+secret+account_number)
 */
export type BankProvider =
  | "paysmart_idfc"
  | "buckzy_global_payout"
  | "razorpayx_inr"
  // "touras_inr" — Touras remittance (per-rail creds: AES key + user id).
  | "touras_inr"
  // "manual" — a bank with NO payout API. Nothing is dispatched over the
  // wire: payouts sit pending until an operator exports them as a CSV, uploads
  // that to the bank's own portal, and records what the bank did. Requires
  // manual_account_no + manual_ifsc on create — not credentials (there is
  // nothing to authenticate to) but the debit side of every file.
  | "manual"
  // "canara_bank" — also no payout API, but unlike "manual" the file layout
  // is fixed by the bank rather than admin-defined. Same debit-side fields.
  | "canara_bank";

/**
 * PublicBankProvider — neutralised provider values returned on the wire by
 * the backend. The backend now strips internal routing identifiers from
 * responses and emits only these neutral tokens. UI code that branches off
 * the response provider should use this union.
 *   - "idfc"       — IDFC bank rail (was "paysmart_idfc")
 *   - "buckzy"     — Buckzy global (was "buckzy_global_payout")
 *   - "razorpayx"  — RazorpayX (was "razorpayx_inr")
 */
/**
 * FileRailSettings — the ordering block a fixed-format file rail stamps on
 * every record of its bulk file. ordering_customer_name and address_line_1 are
 * mandatory before the rail can export; the bank refuses to generate a file
 * without them.
 */
export type FileRailSettings = {
  ordering_customer_name?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  /** Header field 3. Omit for the bank's own default. */
  identifier?: string;
  /** Payees at this bank settle internally rather than by NEFT. */
  on_us_ifsc_prefix?: string;
  /** Narration on the payee's statement. Defaults to PAYOUT. */
  sender_to_receiver_info?: string;
};

export type PublicBankProvider =
  | "idfc"
  | "buckzy"
  | "razorpayx"
  | "touras"
  | "manual"
  | "canara_bank";

/**
 * CreateBankAccountPayload — write-only credential triple plus label and the
 * per-merchant bank-rail endpoint config (base_url + environment). When
 * provider is "buckzy_global_payout", the rail-specific fields are ignored
 * by the backend and may be omitted (audit-pass-9).
 * Credentials are never re-exposed by any response type (T-3-12 mitigate).
 * No account_no / ifsc (D-05).
 */
export interface CreateBankAccountPayload {
  label: string;
  /**
   * Ordering block for a fixed-format file rail (canara_bank). Stamped on
   * every record of the bulk file. ordering_customer_name and address_line_1
   * are mandatory at export time — the bank refuses to generate a file
   * without them — but are accepted empty here so a rail can be created now
   * and completed before its first export.
   */
  file_rail_settings?: FileRailSettings;

  /** Provider — defaults to "paysmart_idfc" if omitted. */
  provider?: BankProvider;
  /** IDFC-rail-only field; required when provider=paysmart_idfc. */
  email?: string;
  /** IDFC-rail-only field; required when provider=paysmart_idfc. */
  password?: string;
  /** IDFC-rail-only field; required when provider=paysmart_idfc. */
  merchant_key?: string;
  /** Bank rail base URL for this merchant — must be https. */
  base_url?: string;
  /** "sandbox" | "production". */
  environment?: string;
  /**
   * Upstream-issued provider merchant id from onboarding (e.g.
   * "29V2155SVGITOGND"). Required when provider=paysmart_idfc. Distinct
   * from `label`, which is operator-chosen display text. Backend rejects
   * with 400 provider_merchant_id_missing when this is empty or
   * whitespace-only.
   * See payouts-backend issue #1 — before this field existed the backend
   * silently treated `label` as the upstream merchant_id, which broke
   * downstream balance / payout calls whenever a friendly label was used.
   */
  provider_merchant_id?: string;
  /**
   * RazorpayX key id ("rzp_live_…" / "rzp_test_…"). Required when
   * provider=razorpayx_inr; ignored otherwise.
   */
  razorpay_key_id?: string;
  /** RazorpayX key secret. Required when provider=razorpayx_inr. */
  razorpay_key_secret?: string;
  /**
   * RazorpayX virtual account number — the source account on every payout
   * call. Distinct from the underlying settlement bank account. Required
   * when provider=razorpayx_inr.
   */
  razorpay_account_number?: string;
  /**
   * Touras rail. The base64 AES-256 key Touras emails at onboarding, and
   * the user id ("AGEN…"). Session id and merchant id default to the user
   * id. Base URL empty = UAT; Touras issues production URLs at go-live.
   * Contact mobile/email are the fallback Touras requires on every payout
   * when the payee has none of their own.
   */
  touras_encryption_key?: string;
  touras_uid?: string;
  touras_session_id?: string;
  touras_mid?: string;
  touras_base_url?: string;
  touras_contact_mobile?: string;
  touras_contact_email?: string;
}

/**
 * CreateBankAccountResult — POST /v1/merchants/:id/bank-accounts response.
 * `active` is true when ValidateCredentials succeeded and disabled_at was cleared.
 * `verify_error` is present when active is false.
 */
export interface CreateBankAccountResult {
  bank_account: BankAccount;
  active: boolean;
  verify_error?: string;
}

/**
 * RotateCredentialsPayload — write-only new credential triple plus the
 * per-merchant bank-rail endpoint config for the new credential row.
 * No re-exposure of the old or new credentials in any response.
 */
export interface RotateCredentialsPayload {
  email: string;
  password: string;
  merchant_key: string;
  /** Bank rail base URL for this merchant — must be https. */
  base_url: string;
  /** "sandbox" | "production". */
  environment: string;
  /**
   * Upstream-issued provider merchant id from onboarding. Required on
   * rotation so a credential rotation that changes the provider account
   * also re-anchors the upstream identifier. See
   * CreateBankAccountPayload.provider_merchant_id for the contract
   * rationale.
   */
  provider_merchant_id: string;
}

/**
 * RotateCredentialsResult — POST /v1/merchants/:id/bank-accounts/:aid/credentials response.
 */
export interface RotateCredentialsResult {
  credential_id: number;
  active: boolean;
  verify_error?: string;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * listBankAccounts — GET /v1/merchants/:merchantId/bank-accounts
 * Returns the merchant's bank rails. Requires viewer+ membership.
 */
export async function listBankAccounts(
  merchantId: number,
): Promise<BankAccount[]> {
  const resp = await apiClient.get<{ bank_accounts: BankAccount[] }>(
    `/v1/merchants/${merchantId}/bank-accounts`,
  );
  return resp.data.bank_accounts;
}

/**
 * createBankAccount — POST /v1/merchants/:merchantId/bank-accounts
 * Creates a new bank rail and validates credentials. Requires owner membership.
 */
export async function createBankAccount(
  merchantId: number,
  payload: CreateBankAccountPayload,
): Promise<CreateBankAccountResult> {
  const resp = await apiClient.post<CreateBankAccountResult>(
    `/v1/merchants/${merchantId}/bank-accounts`,
    payload,
  );
  return resp.data;
}

/**
 * updateBankAccount — PATCH /v1/merchants/:merchantId/bank-accounts/:bankAccountId
 *
 * Admin-only at the backend (auth.RequireRole(admin)). Editable fields:
 * `auto_approve` (migration 034), a file rail's ordering block and debit
 * account, and `disabled` — the rail's off switch (migration 050).
 */
export async function updateBankAccount(
  merchantId: number,
  bankAccountId: number,
  payload: {
    auto_approve?: boolean;
    /**
     * Replaces a fixed-format rail's ordering block wholesale. Rejected with
     * 422 not_a_fixed_format_rail on any other provider — settings stored and
     * never read would leave the rail looking configured.
     */
    file_rail_settings?: FileRailSettings;
    /**
     * Corrects the account a file rail's transfers are drawn on. Both fields
     * are required together — an account number and a branch code from
     * different banks is not a partly-correct rail. Rejected on an API rail,
     * where these are bound to the sealed credentials.
     */
    debit_account_no?: string;
    debit_ifsc?: string;
    /**
     * The operator's off switch (backend migration 050). Send with
     * `disabled_reason` when switching a rail off; the reason is ignored when
     * switching it back on and the stored one is kept as history.
     */
    disabled?: boolean;
    disabled_reason?: string;
  },
): Promise<BankAccount> {
  const resp = await apiClient.patch<BankAccount>(
    `/v1/merchants/${merchantId}/bank-accounts/${bankAccountId}`,
    payload,
  );
  return resp.data;
}

/**
 * rotateBankAccountCredentials — POST /v1/merchants/:merchantId/bank-accounts/:bankAccountId/credentials
 * Replaces the active credential set for a rail. Requires owner membership.
 */
export async function rotateBankAccountCredentials(
  merchantId: number,
  bankAccountId: number,
  payload: RotateCredentialsPayload,
): Promise<RotateCredentialsResult> {
  const resp = await apiClient.post<RotateCredentialsResult>(
    `/v1/merchants/${merchantId}/bank-accounts/${bankAccountId}/credentials`,
    payload,
  );
  return resp.data;
}

/**
 * VerifyResult — POST .../bank-accounts/:aid/verify response.
 * `ok` is true when the stored credentials successfully minted an upstream
 * rail token. `verify_error` carries the provider error when `ok` is false.
 */
export interface VerifyResult {
  ok: boolean;
  verify_error?: string;
}

/**
 * verifyBankAccount — POST /v1/merchants/:merchantId/bank-accounts/:bankAccountId/verify
 * Runs a live credential health check (upstream rail token mint) WITHOUT
 * rotating any secrets. A passing check heals a pending rail to active; a
 * failing one never disables a working rail. Requires operator membership.
 */
export async function verifyBankAccount(
  merchantId: number,
  bankAccountId: number,
): Promise<VerifyResult> {
  const resp = await apiClient.post<VerifyResult>(
    `/v1/merchants/${merchantId}/bank-accounts/${bankAccountId}/verify`,
  );
  return resp.data;
}
